/** Site-level zoom applied when a result card is selected. */
export const SELECTED_SITE_MAP_ZOOM = 10;

/**
 * Cap for stepping zoom while a selected pin remains clustered.
 * Matches @googlemaps/markerclusterer SuperClusterAlgorithm default maxZoom.
 */
export const CLUSTER_REVEAL_MAX_ZOOM = 16;

type MapLike = {
  getZoom: () => number | undefined;
  setCenter: (latLng: google.maps.LatLng | google.maps.LatLngLiteral) => void;
  setZoom: (zoom: number) => void;
};

type MarkerLike = {
  getMap: () => unknown;
  getPosition: () => google.maps.LatLng | null | undefined;
};

export type RevealSelectedSiteOnMapOptions = {
  map: MapLike;
  marker: MarkerLike;
  signal?: AbortSignal;
  selectedZoom?: number;
  maxZoom?: number;
  waitForIdle?: (map: MapLike, signal: AbortSignal) => Promise<void>;
};

export function isMarkerVisibleOnMap(marker: MarkerLike, map: MapLike): boolean {
  return marker.getMap() === map;
}

function waitForMapIdle(map: MapLike, signal: AbortSignal): Promise<void> {
  if (signal.aborted) {
    return Promise.reject(new DOMException('Aborted', 'AbortError'));
  }

  if (typeof google === 'undefined' || !google?.maps?.event?.addListenerOnce) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const listener = google.maps.event.addListenerOnce(map as google.maps.Map, 'idle', () => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    });

    const onAbort = () => {
      google.maps.event.removeListener(listener);
      reject(new DOMException('Aborted', 'AbortError'));
    };

    signal.addEventListener('abort', onAbort, { once: true });
  });
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Centers the selected pin and zooms to a fixed level. If the pin is still
 * clustered after that, steps zoom up until the individual marker is visible
 * (or maxZoom is reached).
 */
export async function revealSelectedSiteOnMap({
  map,
  marker,
  signal,
  selectedZoom = SELECTED_SITE_MAP_ZOOM,
  maxZoom = CLUSTER_REVEAL_MAX_ZOOM,
  waitForIdle = waitForMapIdle,
}: RevealSelectedSiteOnMapOptions): Promise<void> {
  const abortSignal = signal ?? new AbortController().signal;
  if (abortSignal.aborted) {
    return;
  }

  const position = marker.getPosition?.();
  if (!position) {
    return;
  }

  try {
    map.setCenter(position);
    map.setZoom(selectedZoom);
    await waitForIdle(map, abortSignal);

    while (!isMarkerVisibleOnMap(marker, map)) {
      if (abortSignal.aborted) {
        return;
      }

      const currentZoom = map.getZoom();
      if (typeof currentZoom !== 'number' || currentZoom >= maxZoom) {
        return;
      }

      map.setZoom(currentZoom + 1);
      await waitForIdle(map, abortSignal);
    }
  } catch (error) {
    if (isAbortError(error)) {
      return;
    }
    throw error;
  }
}
